import React, { useState, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Alert, CircularProgress, Divider,
    LinearProgress, Chip
} from '@mui/material';
import { urlFiles } from '../../config';

/**
 * Modal wyświetlany gdy użytkownik próbuje wgrać niepodpisany plik XML sprawozdania.
 *
 * Wymaga dołączenia pliku oświadczenia podpisanego cyfrowo Profilem Zaufanym
 * przed umożliwieniem finalnego uploadu sprawozdania.
 */
const UnsignedStatementModal = ({
    open,
    statementFile,       // File — sprawozdanie XML bez podpisu
    onCancel,            // () => void
    onConfirm,           // (statementFile, declarationFile) => Promise<void>
    token,
    darkTheme = false,
}) => {
    const [declarationFile, setDeclarationFile] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verificationResult, setVerificationResult] = useState(null);
    // null | { valid: true, signerInfo: string } | { valid: false, error: string }
    const [uploading, setUploading] = useState(false);
    const declarationInputRef = useRef(null);

    const resetState = () => {
        setDeclarationFile(null);
        setVerifying(false);
        setVerificationResult(null);
        setUploading(false);
    };

    const handleCancel = () => {
        resetState();
        onCancel();
    };

    const handleDeclarationSelect = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        setDeclarationFile(file);
        setVerificationResult(null);
        setVerifying(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${urlFiles}/verify-signature`, {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const data = await response.json();

            if (response.ok && data.valid) {
                setVerificationResult({ valid: true, signerInfo: data.signerInfo || 'Podpis zweryfikowany' });
            } else {
                setVerificationResult({ valid: false, error: data.error || 'Weryfikacja podpisu zakończona błędem.' });
            }
        } catch (err) {
            setVerificationResult({ valid: false, error: `Błąd połączenia z serwerem: ${err.message}` });
        } finally {
            setVerifying(false);
        }

        // Reset input aby można było wybrać ten sam plik ponownie
        if (declarationInputRef.current) declarationInputRef.current.value = null;
    };

    const handleConfirm = async () => {
        if (!declarationFile || !verificationResult?.valid) return;
        setUploading(true);
        try {
            await onConfirm(statementFile, declarationFile);
            resetState();
        } finally {
            setUploading(false);
        }
    };

    const canConfirm = declarationFile && verificationResult?.valid && !verifying && !uploading;

    const paperStyle = darkTheme
        ? { backgroundColor: '#1e2329', color: '#fff' }
        : { backgroundColor: '#fff', color: '#0F172A' };

    return (
        <Dialog
            open={open}
            onClose={uploading ? undefined : handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: paperStyle }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <i className="fas fa-file-signature" style={{ color: '#f59e0b', fontSize: '1.2rem' }} />
                    <Typography variant="h6" fontWeight={700}>
                        Sprawozdanie bez podpisu cyfrowego
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Informacja o sprawozdaniu */}
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Plik <strong>{statementFile?.name}</strong> nie posiada podpisu cyfrowego.
                    Aby go załączyć, musisz dołączyć <strong>oświadczenie podpisane cyfrowo
                    Profilem Zaufanym</strong>.
                </Alert>

                {/* Sekcja oświadczenia */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Oświadczenie (wymagane)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Wybierz plik oświadczenia podpisany Profilem Zaufanym (wyłącznie format .pdf).
                        System automatycznie zweryfikuje poprawność podpisu.
                    </Typography>

                    <input
                        ref={declarationInputRef}
                        id="declaration-file-input"
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={handleDeclarationSelect}
                        disabled={verifying || uploading}
                    />
                    <label htmlFor="declaration-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            disabled={verifying || uploading}
                            startIcon={<i className="fas fa-paperclip" />}
                        >
                            Wybierz plik oświadczenia
                        </Button>
                    </label>

                    {declarationFile && (
                        <Chip
                            label={declarationFile.name}
                            variant="outlined"
                            size="small"
                            sx={{ ml: 1.5, maxWidth: 260, overflow: 'hidden' }}
                        />
                    )}
                </Box>

                {/* Status weryfikacji */}
                {verifying && (
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Weryfikacja podpisu cyfrowego...</Typography>
                    </Box>
                )}

                {verificationResult?.valid && (
                    <Alert severity="success" sx={{ mb: 1.5 }}>
                        <strong>Podpis Profilu Zaufanego zweryfikowany poprawnie.</strong>
                        {verificationResult.signerInfo && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Podpisał: {verificationResult.signerInfo}
                            </Typography>
                        )}
                    </Alert>
                )}

                {verificationResult?.valid === false && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        <strong>Weryfikacja podpisu zakończona błędem.</strong>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {verificationResult.error}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            Upewnij się, że oświadczenie jest podpisane cyfrowo Profilem Zaufanym.
                        </Typography>
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Podsumowanie co zostanie wysłane */}
                <Typography variant="body2" color="text.secondary">
                    Po potwierdzeniu zostaną przesłane:
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                    <Typography component="li" variant="body2">
                        Sprawozdanie: <strong>{statementFile?.name}</strong> (bez podpisu)
                    </Typography>
                    <Typography component="li" variant="body2" color={canConfirm ? 'success.main' : 'text.disabled'}>
                        Oświadczenie: <strong>{declarationFile?.name ?? '— nie wybrano'}</strong>
                    </Typography>
                </Box>

                {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleCancel}
                    disabled={uploading}
                >
                    Anuluj
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    startIcon={uploading
                        ? <CircularProgress size={16} color="inherit" />
                        : <i className="fas fa-upload" />
                    }
                >
                    {uploading ? 'Przesyłanie...' : 'Załącz sprawozdanie'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnsignedStatementModal;
